import React from "react";
import { TfiTimer } from "react-icons/tfi";
import styles from "./index.module.css";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import {
  cancelOpportunityApi,
  cancelTrancheV2,
  createTrancheV2,
} from "../../service/api";
import { showNotification } from "../../service/showNotification";
import Timer from "./Timer";
import TrancheApprovalSend from "../TranchApprovalSend/TrancheApprovalSend";
import clsx from "clsx";
import { GoShieldCheck } from "react-icons/go";
import { RxCross2 } from "react-icons/rx";
import { useHistory } from "react-router-dom";
import { TrancheBotV2Status } from "../../utils/constant";
import { FaArrowLeftLong } from "react-icons/fa6";

const ReviewSummaryTable = ({ data }) => {
  return (
    <div className={styles.reviewSummaryContainer}>
      <table className={styles.reviewSummaryTable}>
        <thead>
          <tr>
            <th>Metric</th>
            <th className={styles.proposedAssetValues}>Proposed</th>
            <th className={styles.proposedAssetValues}>Actual</th>
            <th className={styles.proposedAssetValues}>Difference</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td>{row.metric}</td>
              <td className={styles.proposedAssetValues}>{row.proposed}</td>
              <td className={styles.proposedAssetValues}>{row.actual}</td>
              <td className={styles.difference}>{row.difference}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AssetDetailsRows = ({ rows }) => {
  return (
    <>
      {rows.map((asset, index) => (
        <div key={index} className={styles.assetDetailsRow}>
          <div>
            <span className={styles.assetDetailsName}>ID:</span>
            <span className={styles.assetDetailsValue}> {asset.assetId}</span>
          </div>
          <div>
            <span className={styles.assetDetailsName}>ITC %:</span>
            <span className={styles.assetDetailsValue}> {asset.itc}%</span>
          </div>
          <div>
            <span className={styles.assetDetailsName}>Price:</span>
            <span className={styles.assetDetailsValue}>
              {" "}
              ${asset.price?.toLocaleString()}
            </span>
          </div>
          <div>
            <span className={styles.assetDetailsName}>State:</span>
            <span className={styles.assetDetailsValue}> {asset.state}</span>
          </div>
          <div>
            <span className={styles.assetDetailsName}>Provider:</span>
            <span className={styles.assetDetailsValue}>
              {" "}
              {asset.dealSource}
            </span>
          </div>
        </div>
      ))}
    </>
  );
};

const OpportunityCard = ({
  opportunity,
  onCancelSuccess,
  onTrancheCreated,
  hasAnyMatch,
}) => {
  const [loading, setLoading] = React.useState("");
  const [isExpired, setIsExpired] = React.useState(false);
  const hasAssets = opportunity?.selectedAssets?.length > 0;
  const history = useHistory();

  React.useEffect(() => {
    if (opportunity?.expiresOn) {
      const expireTime = new Date(opportunity.expiresOn).getTime();
      const now = Date.now();
      const diffInHours = (expireTime - now) / (1000 * 60 * 60); // ms -> hr

      if (diffInHours > 48) {
        setIsExpired(true);
      } else {
        setIsExpired(false);
      }
    }
  }, [opportunity?.expiresOn]);

  const reviewData = [
    {
      metric: "Equity Contribution",
      proposed: opportunity?.ecToDeploy
        ? `$${opportunity.ecToDeploy.toLocaleString()}`
        : "-",
      actual:
        hasAssets && opportunity?.totalPurchaseAmount
          ? `$${opportunity.totalPurchaseAmount.toLocaleString()}`
          : "$0",
      difference:
        hasAssets && opportunity?.ecToDeploy !== undefined
          ? `$${(
              (opportunity.totalPurchaseAmount || 0) - opportunity.ecToDeploy
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} (${(
              (((opportunity.totalPurchaseAmount || 0) -
                opportunity.ecToDeploy) /
                opportunity.ecToDeploy) *
              100
            ).toFixed(2)}%)`
          : "-",
    },
    {
      metric: "State",
      proposed: opportunity?.stateString || "-",
      actual:
        hasAssets && opportunity?.stateString ? opportunity.stateString : "-",
      difference:
        hasAssets && opportunity?.stateString && opportunity?.stateString
          ? opportunity.stateString === opportunity.stateString
            ? "Match"
            : "Mismatch"
          : "-",
    },
    {
      metric: "ITC %",
      proposed: opportunity?.itcPercent ? `${opportunity.itcPercent}%` : "-",
      actual:
        hasAssets && opportunity?.overallItcPercentage
          ? `${opportunity.overallItcPercentage}%`
          : "0%",
      difference:
        hasAssets && opportunity?.itcPercent !== undefined
          ? `${(
              (opportunity.overallItcPercentage || 0) - opportunity.itcPercent
            ).toFixed(2)}%`
          : "-",
    },
    {
      metric: "Asset Availability",
      actual: hasAssets ? (
        <>
          <IoMdCheckmarkCircleOutline
            style={{
              color: "#00A300",
              marginRight: "5px",
              marginBottom: "2px",
            }}
          />
          Confirmed
        </>
      ) : (
        "No"
      ),
    },
  ];

  const handleCreateTranche = async (trancheId) => {
    try {
      setLoading("create_tranche");
      const res = await createTrancheV2({ id: trancheId });
      showNotification("success", "Tranche created successfully!");
      if (onTrancheCreated) {
        onTrancheCreated(res.data); // pass response back up
      }
    } catch (error) {
      showNotification("error", "Failed to create tranche.");
    } finally {
      setLoading("");
    }
  };

  const handleCancel = async (tranchId) => {
    console.log("Cancelling opportunity:", tranchId);
    try {
      setLoading("cancel_tranche");
      await cancelTrancheV2(tranchId); // <-- pass only the string ID
      showNotification("success", "Tranche Cancelled Successfully");

      if (tranchId) {
        onCancelSuccess(tranchId);
      }
    } catch (err) {
      console.error("Cancel failed:", err);
      showNotification("error", "Failed to cancel opportunity");
    } finally {
      setLoading("");
    }
  };

  const cancelTrancheLoader = loading === "cancel_tranche";
  const createTrancheLoader = loading === "create_tranche";

  const isPending = opportunity.status === TrancheBotV2Status.PENDING;

  const pvAssetList = opportunity.selectedAssets?.filter(asset => !!asset?.systemSizeKwValue)
  const storageAssetList = opportunity.selectedAssets?.filter(asset => !asset?.systemSizeKwValue)

  return (
    <div className={styles.opportunityCard}>
      <div className="d-flex w-100 justify-content-start align-items-center mb-4 text-start">
        <button className={styles.backButton} onClick={() => history.goBack()}>
          <FaArrowLeftLong /> Back
        </button>
      </div>
      {hasAnyMatch && (
        <div className={styles.proposedAssetsTimerContainer}>
          <Timer expireTime={new Date(opportunity.expiresOn).getTime()} />
          <div>
            <p className={styles.proposedAssetsTimerParagraph}>
              Time left to approve this tranche.
            </p>
          </div>
        </div>
      )}
      {/* Heading block */}
      <div className={styles.proposedAssetsDiv}>
        <h1 className={styles.proposedAssetsHeaderMain}>Proposed Assets</h1>
        <p className={styles.proposedAssetsParagraph}>
          Review the proposed assets for{" "}
          <strong>{opportunity.trancheName}</strong>
        </p>
      </div>

      {/* Review Summary + Asset Details */}
      <div className={styles.proposedAssetsReviewSummary}>
        <h1 className={styles.proposedAssetsHeader}>Review Summary</h1>
        <ReviewSummaryTable data={reviewData} />

        {hasAssets ? (
          <>
            <h1 className={styles.proposedAssetsHeader}>
              {!!storageAssetList?.length && 'Storage Assets'}
            </h1>
            <AssetDetailsRows rows={storageAssetList} />

            <h1 className={styles.proposedAssetsHeader}>
              {!!pvAssetList?.length && 'PV Assets'}
            </h1>
            <AssetDetailsRows rows={pvAssetList} />
          </>
        ) : (
          <>
            <h1 className={styles.proposedAssetsHeader}>Asset Details</h1>
            <p className={styles.noMatchText}>
              No assets available for this opportunity.
            </p>
          </>
        )}
      </div>

      {/* Show action buttons only when assets exist */}
      {hasAssets && isPending && (
        <div className={styles.buttonGroup}>
          <button
            className={clsx(styles.proposedAssetsButton, styles.cancelButton)}
            onClick={() => handleCancel(opportunity._id)}
            disabled={cancelTrancheLoader}
          >
            <RxCross2 className={styles.icon} />
            {cancelTrancheLoader ? "Cancel..." : "Cancel"}
          </button>
          <button
            className={clsx(styles.proposedAssetsButton, styles.createButton)}
            onClick={() => handleCreateTranche(opportunity._id)}
            disabled={createTrancheLoader}
          >
            <GoShieldCheck className={styles.icon} />
            {createTrancheLoader ? "Creating..." : "Create Tranche"}
          </button>
        </div>
      )}
    </div>
  );
};

const ProposedAssets = ({ pollingData }) => {
  const history = useHistory();
  const [results, setResults] = React.useState(() => {
    return (
      pollingData?.data?.results?.map((opp) => ({
        ...opp,
        selectedAssets:
          opp.assets?.map((asset) => ({
            assetId: asset.assetId,
            itc: asset.itcPercent,
            price: asset.price,
            state: asset.state,
            dealSource: asset.dealSource,
            systemSizeKwValue: asset?.systemSizeKwValue,
            batteryKwhSizeValue: asset?.batteryKwhSizeValue
          })) || [],
        overallItcPercentage: opp.totalItcPercent || 0,
        totalSelectedAssets: opp.assets?.length || 0,
        targetAmount: opp.ecToDeploy,
      })) || []
    );
  });

  React.useEffect(() => {
    // sync when new polling data arrives
    if (pollingData?.data?.results) {
      setResults((prev) => {
        const newResults = pollingData.data.results.map((opp) => ({
          ...opp,
          selectedAssets:
            opp.assets?.map((asset) => ({
              assetId: asset.assetId,
              itc: asset.itcPercent,
              price: asset.price,
              state: asset.state,
              dealSource: asset.dealSource,
              systemSizeKwValue: asset?.systemSizeKwValue,
              batteryKwhSizeValue: asset?.batteryKwhSizeValue
            })) || [],
          overallItcPercentage: opp.totalItcPercent || 0,
          totalSelectedAssets: opp.assets?.length || 0,
          targetAmount: opp.ecToDeploy,
        }));

        // merge avoiding duplicates
        const merged = [
          ...prev,
          ...newResults.filter((nr) => !prev.some((p) => p._id === nr._id)),
        ];
        return merged;
      });
    }
  }, [pollingData]);

  const handleCancelSuccess = (opportunityId) => {
    setResults((prev) => prev.filter((item) => item._id !== opportunityId));
  };
  const handleTrancheCreated = (updatedTranche) => {
    setResults((prev) =>
      prev.map((item) =>
        item._id === updatedTranche._id ? { ...item, ...updatedTranche } : item
      )
    );
  };
  const handleApprovalSent = (trancheId) => {
    setResults((prev) =>
      prev.map((item) =>
        item._id === trancheId ? { ...item, status: "Sent for Approval" } : item
      )
    );
  };

  const totalJobs =
    pollingData?.data?.jobCount + results.length || results.length;

  if (results.length === 0) {
    history.push("/tranche-bot-v2");
  }

  if (!results.length && totalJobs === 0) return null;

  const hasAnyMatch = results.some((r) => r.selectedAssets?.length > 0);

  return (
    <div className={styles.proposedAssetsContainer}>
      {results.map((opp) =>
        opp.status === TrancheBotV2Status.ACTIVE ? (
          <TrancheApprovalSend
            key={opp._id}
            trancheData={opp}
            onApprovalSent={handleApprovalSent}
          />
        ) : (
          <OpportunityCard
            key={opp._id}
            opportunity={opp}
            hasAnyMatch={hasAnyMatch}
            onTrancheCreated={handleTrancheCreated}
            onCancelSuccess={handleCancelSuccess}
          />
        )
      )}

      {/* 🔁 Show loaders for remaining jobs */}
      {Array.from({ length: totalJobs - results.length }).map((_, i) => (
        <div key={`loader-${i}`} className={styles.loaderCard}>
          <div className={styles.spinner}></div>
          <p>Waiting for results...</p>
        </div>
      ))}
    </div>
  );
};

export default ProposedAssets;
